/**
 * Settings root page (v1.1.3-b4 / Wave 6d).
 *
 * Replaces the legacy ProfileSheet slide-up drawer. The gear icon on the
 * Trainer profile (/) now routes here instead of opening the drawer.
 *
 * Sections mirror the migration audit:
 *   - Account: trainer handle, trainer level (Wave 6c carryover),
 *     email (read-only)
 *   - Subscription: link to /settings/billing + iOS manage-subscription
 *     deep link
 *   - Appearance: theme (auto/light/dark) + Design v2 preview toggle
 *   - Notifications: master switch + per-channel toggles
 *   - Friend visibility: presence + trainer-code-public + dex visibility
 *   - Trade search template
 *   - Event feed auto-refresh
 *   - Privacy and data: download / withdraw / delete / uploaded images
 *   - Nex companion priorities
 *   - Demo: tier switcher (kept until shipping build)
 *   - About / legal: re-open legal disclaimer, full legal pack
 *   - Sign out
 */
import { memo, useCallback, useEffect, useMemo, useState, createContext, useContext } from "react";
import { Capacitor } from "@capacitor/core";
import { Link, useLocation } from "wouter";
import { AppShell } from "@/components/AppShell";
import { DotRail } from "@/components/DotRail";
import { MenuHeading } from "@/components/MenuHeading";
import { Button } from "@/components/ui/button";
import { TrainerCodeField } from "@/components/TrainerCodeField";
import { Switch } from "@/components/ui/switch";
import { useToast } from "@/hooks/use-toast";
import {
  fireLocalNotification,
  requestNotificationPermission,
} from "@/lib/notifications";
import { deviceChannel, setDeviceChannel } from "@/lib/localNotifPrefs";
import { isNative as isNativeShell, getPlatform } from "@/lib/platform";
import { LeaveReasonPicker } from "@/components/LeaveReasonPicker";
import { leaveRequestBody, type LeaveReason } from "@/lib/leaveReasons";
import { isRemotePushAvailable, probeRemotePushAvailability } from "@/lib/notifications";
import { previewLiveActivity } from "@/lib/liveActivities";
import { LiveEventsSettingsGroup } from "@/components/LiveEventsSettingsGroup";
import { canLeaveStoreReview, openStoreReview, resetRatingAsk, storeReviewName } from "@/lib/storeReview";
import { isFirstRunPromptDone, lastFirstRunPromptAskedAt } from "@/lib/firstRunPrompts";
import { copyToClipboard } from "@/lib/copyToClipboard";
import { usePrices, formatPlanLine } from "@/hooks/usePrices";
import { useAuth } from "@/components/AuthProvider";
import { TrainerInlineEditor } from "@/components/TrainerFields";
import { useSession, hasProAccess, type AccentTheme, type ThemeMode, type Tier } from "@/components/SessionProvider";
import { proHeadlineRows } from "@/lib/proFeatureRows";
import { openWhyPro } from "@/components/WhyProSheet";
import { useInventory } from "@/components/InventoryProvider";
import { trainerStatRows, type TrainerProfileDto } from "@/lib/trainerStats";
import { LegalReader } from "@/components/LegalReader";
import { BroadcastDiagnosticsPanel } from "@/components/BroadcastDiagnosticsPanel";
import { IngestionDiagnosticsPanel } from "@/components/IngestionDiagnosticsPanel";
import { OcrResultsPanel } from "@/components/OcrResultsPanel";
import { PaywallDiagnosticsPanel } from "@/components/PaywallDiagnosticsPanel";
import { useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { APP_VERSION_STRING } from "@/lib/version";
import {
  isDiagnosticsMenuEnabled,
  __resetDiagnosticsMenuCache,
  DIAGNOSTICS_MENU_LS_KEY,
} from "@/lib/diagnosticsMenuFlag";
import { useV2Theme, setV2Theme } from "@/lib/v2Theme";
import { ProfilePriorityRadar } from "@/components/ProfilePriorityRadar";
import {
  BOTTOM_SLOT_OPTIONS,
  readBottomSlot,
  setBottomSlot,
} from "@/lib/bottomSlotPref";
import {
  NOTIF_EVENTS,
  deriveChannel,
  channelPatch,
  type NotifChannel,
  DEVICE_NOTIF_EVENTS,
  type NotifEvent,
  type DeviceNotifId,
} from "@/lib/notifChannels";
import { API_BASE } from "@/lib/apiBase";
import { resetNexusDex } from "@/lib/dexStateStore";
import { isDexAutoUpdateEnabled, setDexAutoUpdateEnabled } from "@/lib/dexAutoUpdatePref";
import { isAutoScanOnOpenEnabled, setAutoScanOnOpenEnabled } from "@/lib/autoScanOnOpenPref";
import { ensureScreenshotAccess, isScreenshotSweepAvailable, openAppSettings } from "@/lib/screenshotLibrary";
import { ToastAction } from "@/components/ui/toast";
import { isFriendTradesEnabled, setFriendTradesEnabled } from "@/lib/friendTradesPref";
import {
  MY_POKEMON_BACKUP_NOW_EVENT,
  setMyPokemonBackupEnabled,
  useMyPokemonBackupStatus,
} from "@/lib/myPokemonBackup";
import { myPokemonCount } from "@/lib/myPokemonStore";
import { getDexScanCaptureText, dexScanCaptureLineCount, clearDexScanCapture } from "@/lib/dexScanLog";
import { getDexFrameDumpText, dexFrameDumpCount } from "@/lib/dexFrameDump";
import { isScanLogExportAccount, isScanLogExportUnlocked, unlockScanLogExport } from "@/lib/scanLogExportUnlock";
import {
  orderedVariants,
  LOCKED_ON_VARIANTS,
  VISIBILITY_EVENT,
  isDexVisible,
  setDexVisible,
} from "@/lib/dexVisibilityStore";
import { getVariantLabel } from "@/components/dexVariantVisuals";
import {
  isCustomDexLinkageEnabled,
  setCustomDexLinkageEnabled,
  CUSTOM_DEX_LINKAGE_EVENT,
} from "@/lib/customDexLinkagePref";
import type { PokedexVariant } from "@/lib/pogoPokedexVariantClassifier";
import { clearStoredSprites, formatBytes, storedSpriteUsage } from "@/lib/storedSpriteCleanup";
import {
  ArrowRight, AtSign, CreditCard, ExternalLink, Lock,
  Shield, FileText, LogOut, Loader2, Sun, Moon, Monitor,
  Users as UsersIcon, Trash2, ImageOff, ChevronRight,
  Download, ShieldOff, Star, ChevronDown, ChevronUp,
  Crown, Activity, RotateCcw,
} from "lucide-react";

type AccountSettings = {
  accountId: number;
  notificationsEnabled: boolean;
  notifFriendRequest: boolean;
  notifFriendAccepted: boolean;
  notifFriendDexProgress: boolean;
  notifCollectionMilestone: boolean;
  notifTrades: boolean;
  friendCodePublic: boolean;
  presenceVisible: boolean;
  tradeSearchTemplate: string | null;
  eventFeedAutoRefresh: boolean;
};

// Mirrors notification_preferences row in the worker. `collectionMilestones`
// (N-06) defaults ON; `friendDexProgress` (N-03) defaults OFF so push is
// opt-in. Quiet hours / permission timestamps default null.
type PushPreferences = {
  accountId: number;
  collectionMilestones: boolean;
  friendDexProgress: boolean;
  friendRequests: boolean;
  friendAccepted: boolean;
  trades: boolean;
  quietStart: number | null;
  quietEnd: number | null;
  timeZone: string;
  permissionGrantedAt: number | null;
  permissionDeniedAt: number | null;
  updatedAt: number;
};

export default function Settings() {
  const [, setLocation] = useLocation();
  const { account, signout, isSignedIn, token } = useAuth();
  const {
    themeMode, setThemeMode, accentTheme, setAccentTheme, tier, setTier,
    showLegal, openPaywall, trialSearchesLeft, tourOpen,
    presenceVisible, setPresenceVisible,
  } = useSession();
  const { trainer } = useInventory();
  const { prices } = usePrices();
  const { toast } = useToast();

  const [friendTrades, setFriendTradesLocal] = useState(() => isFriendTradesEnabled());
  const [aboutExpanded, setAboutExpanded] = useState(false);
  // Which destination the fourth bottom-bar slot points at.
  const [bottomSlot, setBottomSlotState] = useState(readBottomSlot);
  const [legalReaderOpen, setLegalReaderOpen] = useState(false);

  // Live Scan diagnostics panel. There are two ways to land here:
  //   1) Tap the labelled "Live Scan diagnostics" card in this page (the
  //      production entry point for QA / beta testers).
  //   2) Arrive from the LiveScanModal footer link, which navigates to
  //      `#/settings?open=diagnostics` so the panel is already expanded.
  // The legacy "tap version 5 times" easter egg below still works for
  // muscle memory, but the labelled entry point is the source of truth.
  const [diagTapCount, setDiagTapCount] = useState(0);
  // Runtime unlock trigger. The diagnostics surface is gated behind
  // `import.meta.env.DEV || isDiagnosticsMenuEnabled()`, but on a prod
  // build the flag is resolved (and cached) at module load before the
  // user has tapped anything. Once the 7-tap gesture sets the
  // localStorage override and clears the cache, we still need a React
  // state change to re-render the gate. This boolean is that trigger.
  const [diagUnlocked, setDiagUnlocked] = useState(false);
  const diagAvailable = import.meta.env.DEV || isDiagnosticsMenuEnabled() || diagUnlocked;
  // "Export scan log" for a Pro trainer: revealed by the same seven taps,
  // held in memory only (lib/scanLogExportUnlock), so it is back to hidden
  // after the app is closed. The admin account and the owner's test handles
  // (SCAN_LOG_EXPORT_HANDLES) see it without the gesture.
  const [scanLogUnlocked, setScanLogUnlocked] = useState(isScanLogExportUnlocked);
  // Android remote push exists only on a Firebase-configured build
  // (2026-09-30); the native probe answers once per session.
  const [remotePushReady, setRemotePushReady] = useState(isRemotePushAvailable);
  useEffect(() => {
    void probeRemotePushAvailability().then((ok) => setRemotePushReady(ok));
  }, []);
  const showScanLogExport =
    Boolean(account?.isAdmin) ||
    isScanLogExportAccount(account?.username, account?.id) ||
    (scanLogUnlocked && hasProAccess(tier));
  // Admin only: when the store rating sheet was last asked for on this device,
  // re-read after "Reset rating prompts" (lib/storeReview.resetRatingAsk).
  // The rating ask is once per device (2026-10-26): a dated showing, the old
  // card's undated flag, or never.
  const reviewAskedLabel = (): string => {
    const at = lastFirstRunPromptAskedAt("review");
    if (at != null) return `Asked for on this device on ${new Date(at).toLocaleDateString()}. It will not ask again.`;
    if (isFirstRunPromptDone("review")) return "Asked by the earlier review card on this device. It will not ask again.";
    return "Never asked for on this device.";
  };
  const [ratingAskedLabel, setRatingAskedLabel] = useState<string>(reviewAskedLabel);
  const [diagPanelOpen, setDiagPanelOpen] = useState(() => {
    // The diagnostics surface only exists in dev/QA builds and on prod
    // builds where the diagnostics-menu override is set (see the
    // `import.meta.env.DEV || isDiagnosticsMenuEnabled()` gate below), so
    // never auto-open it when the surface is unreachable.
    if (!(import.meta.env.DEV || isDiagnosticsMenuEnabled())) return false;
    try {
      if (typeof window === "undefined") return false;
      const hash = window.location.hash || "";
      // Hash router uses `#/settings?open=diagnostics`. Match either the
      // query-style param or a plain `#diagnostics` fragment for safety.
      return /[?&]open=diagnostics\b/.test(hash) || /#diagnostics\b/.test(hash);
    } catch {
      return false;
    }
  });
  // If the diagnostics panel was deep-linked open, scroll it into view on
  // mount so the tester doesn't land at the top of a long Settings page
  // wondering where it went.
  useEffect(() => {
    if (!diagPanelOpen) return;
    const t = setTimeout(() => {
      const el = document.querySelector('[data-testid="settings-section-diagnostics"]');
      if (el && typeof (el as HTMLElement).scrollIntoView === "function") {
        (el as HTMLElement).scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }, 50);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // Measured on mount, and again after a clear, so the line always describes
  // what is actually on the device rather than what was there at boot.
  const [spriteUsage, setSpriteUsage] = useState(() => storedSpriteUsage());
  const [confirmResetDex, setConfirmResetDex] = useState(false);
  const [resettingDex, setResettingDex] = useState(false);

  // Workstream C launch readiness: Apple requires an in-app account deletion
  // flow at the bottom of Settings. The backend already supports a 15-day
  // soft-delete (POST /api/account/delete) that the auth route auto-cancels
  // on sign-in. This state drives the inline "Confirm deletion" panel that
  // surfaces below the destructive button.
  const [accountDeleteOpen, setAccountDeleteOpen] = useState(false);
  const [accountDeletePending, setAccountDeletePending] = useState(false);
  const [accountDeleteScheduled, setAccountDeleteScheduled] = useState<{
    requestedAt: number;
    deleteAt: number;
  } | null>(null);
  // "Why are you leaving?" (2026-10-01): optional, sent with the request,
  // never blocks it. Cleared whenever the confirm panel closes.
  const [leaveReason, setLeaveReason] = useState<LeaveReason | null>(null);
  const [leaveDetail, setLeaveDetail] = useState("");
  const closeAccountDelete = () => {
    setAccountDeleteOpen(false);
    setLeaveReason(null);
    setLeaveDetail("");
  };

  // Fetch current delete-scheduled status so a returning user sees the
  // remaining cool-off window instead of being prompted to delete again.
  const deleteStatusQuery = useQuery<{
    scheduled: boolean;
    requestedAt?: number;
    deleteAt: number | null;
  }>({
    queryKey: ["/api/account/delete/status"],
    enabled: isSignedIn,
  });
  useEffect(() => {
    const data = deleteStatusQuery.data;
    if (data?.scheduled && data.deleteAt && data.requestedAt) {
      setAccountDeleteScheduled({ requestedAt: data.requestedAt, deleteAt: data.deleteAt });
    } else if (data && !data.scheduled) {
      setAccountDeleteScheduled(null);
    }
  }, [deleteStatusQuery.data]);

  async function requestAccountDeletion() {
    if (accountDeletePending) return;
    setAccountDeletePending(true);
    try {
      const res = await apiRequest("POST", "/api/account/delete", leaveRequestBody(leaveReason, leaveDetail));
      const body = (await res.json().catch(() => null)) as {
        requestedAt?: number;
        deleteAt?: number;
      } | null;
      if (body?.requestedAt && body.deleteAt) {
        setAccountDeleteScheduled({ requestedAt: body.requestedAt, deleteAt: body.deleteAt });
      }
      closeAccountDelete();
      toast({
        title: "Account deletion scheduled",
        description:
          "We will hold your data for 15 days. Sign in within 15 days to cancel. Signing out now.",
      });
      queryClient.invalidateQueries({ queryKey: ["/api/account/delete/status"] });
      // Apple App Review specifically calls out that deletion must feel like
      // deletion. Sign the user out of the device so the next launch lands
      // on the Sign-In screen and the trainer can't accidentally cancel the
      // request just by tapping around.
      try { await signout(); } catch { /* never block the success path */ }
      setLocation("/");
    } catch {
      toast({
        title: "Could not schedule deletion",
        description: "Check your connection and try again.",
        variant: "destructive",
      });
    } finally {
      setAccountDeletePending(false);
    }
  }

  async function cancelAccountDeletion() {
    setAccountDeletePending(true);
    try {
      await apiRequest("POST", "/api/account/delete/cancel");
      setAccountDeleteScheduled(null);
      queryClient.invalidateQueries({ queryKey: ["/api/account/delete/status"] });
      toast({ title: "Deletion cancelled", description: "Your account is no longer scheduled for removal." });
    } catch {
      toast({
        title: "Could not cancel",
        description: "Sign in again and retry, or contact support.",
        variant: "destructive",
      });
    } finally {
      setAccountDeletePending(false);
    }
  }

  // Account settings (server)
  const settingsQuery = useQuery<AccountSettings>({
    queryKey: ["/api/account/settings"],
    enabled: isSignedIn,
  });

  // The trainer's profile row, straight from the cloud: the typed level, plus
  // whatever a profile screenshot captured on builds before 2026-10-02. /api/settings/all already returns the whole
  // trainer_profiles row as `trainer`, so this needs no new endpoint — it is
  // the same payload SessionProvider hydrates from on sign-in.
  const trainerStatsQuery = useQuery<{ trainer: TrainerProfileDto | null }>({
    queryKey: ["/api/settings/all"],
    enabled: isSignedIn,
  });
  const trainerStats = trainerStatsQuery.data?.trainer ?? null;
  // Everything but the level, which the name-and-level row already shows.
  const otherTrainerStats = trainerStatRows(trainerStats).filter((r) => r.label !== "Level");
  const settingsMutation = useMutation({
    mutationFn: async (patch: Partial<AccountSettings>) => {
      const res = await apiRequest("PATCH", "/api/account/settings", patch);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/account/settings"] });
    },
    onError: () => toast({ title: "Could not update settings", variant: "destructive" }),
  });
  const patchSetting = (patch: Partial<AccountSettings>) => settingsMutation.mutate(patch);
  const settings = settingsQuery.data;
  const notifs = settings?.notificationsEnabled ?? true;

  // ── Workstream D — push notification preferences (server-side) ────────────
  // The server is the source of truth for per-type opt-outs so a reinstall
  // restores the trainer's choices. The actual APNs send still no-ops until
  // Sam's .p8 key lands and the worker secrets are populated.
  const pushPrefsQuery = useQuery<{
    preferences: PushPreferences;
    apnsConfigured: boolean;
  }>({
    queryKey: ["/api/notifications/preferences"],
    enabled: isSignedIn,
  });
  const pushPrefsMutation = useMutation({
    mutationFn: async (patch: Partial<PushPreferences>) => {
      const res = await apiRequest("PATCH", "/api/notifications/preferences", patch);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/notifications/preferences"] });
    },
    onError: () =>
      toast({ title: "Could not update push preferences", variant: "destructive" }),
  });
  const patchPushPref = (patch: Partial<PushPreferences>) =>
    pushPrefsMutation.mutate(patch);
  const pushPrefs = pushPrefsQuery.data?.preferences;

  // ── Merged notification channel model ────────────────────────────────────
  // One row per event type, each with an Off / In-app / Push choice. We KEEP
  // both backing stores (accountSettings notif* + notificationPreferences) and
  // derive/patch the channel from them — see client/src/lib/notifChannels.ts.
  const notifStores = {
    inApp: (settings ?? {}) as Record<string, boolean | undefined>,
    push: (pushPrefs ?? {}) as Record<string, boolean | undefined>,
  };
  const setNotifChannel = (
    event: (typeof NOTIF_EVENTS)[number],
    channel: NotifChannel,
  ) => {
    const { inAppPatch, pushPatch } = channelPatch(event, channel);
    if (inAppPatch) patchSetting(inAppPatch as Partial<AccountSettings>);
    if (pushPatch) patchPushPref(pushPatch as Partial<PushPreferences>);
  };

  // Device-raised events keep their choice on this phone (lib/localNotifPrefs).
  const [deviceChannels, setDeviceChannels] = useState<Record<string, NotifChannel>>(() =>
    Object.fromEntries(DEVICE_NOTIF_EVENTS.map((e) => [e.id, deviceChannel(e.id as DeviceNotifId)])),
  );
  const channelFor = (event: NotifEvent): NotifChannel =>
    event.scope === "device"
      ? (deviceChannels[event.id] ?? event.deviceDefault ?? "push")
      : deriveChannel(event, notifStores);

  // Choosing Push on a phone asks the OS first (2026-09-11). A refusal keeps
  // the row on In-app and says where to turn notifications on, so a "Push"
  // that could never arrive is never shown as chosen.
  const chooseChannel = async (event: NotifEvent, channel: NotifChannel) => {
    let next = channel;
    if (channel === "push" && isNativeShell()) {
      const perm = await requestNotificationPermission({ reprompt: true });
      if (perm !== "granted") {
        next = "in-app";
        toast({
          title: "Notifications are off for Nexus Dex.",
          description: "Turn them on for Nexus Dex in your phone's Settings, then choose Push again.",
          variant: "destructive",
        });
      }
    }
    if (event.scope === "device") {
      setDeviceChannel(event.id as DeviceNotifId, next);
      setDeviceChannels((prev) => ({ ...prev, [event.id]: next }));
    } else {
      setNotifChannel(event, next);
    }
  };

  // "Send me an example": the row's sample copy, through the row's channel,
  // so the trainer sees exactly what each kind looks like on this phone.
  const previewEvent = async (event: NotifEvent, index: number) => {
    const channel = channelFor(event);
    if (channel === "off") {
      toast({ title: `${event.label} is off.`, description: "Choose In-app or Push to see an example." });
      return;
    }
    // On a phone the event reminder IS the countdown card (2026-10-23): the
    // example is the card itself, an hour from now.
    if (channel === "push" && event.id === "event-reminders" && isNativeShell()) {
      const live = await previewLiveActivity();
      if (live.ok) {
        toast({ title: "Countdown on your Lock Screen.", description: live.detail });
        return;
      }
      // Say why the card could not start (Live Activities off for Nexus Dex,
      // an older iPhone); the example notification below still shows.
      toast({ title: "No countdown on this phone.", description: live.detail });
    }
    toast({ title: event.example.title, description: event.example.body });
    if (channel === "push") {
      const fired = await fireLocalNotification({ id: 900100 + index, title: event.example.title, body: event.example.body });
      if (!fired.ok && fired.detail !== "web") {
        toast({
          title: "The phone did not show it.",
          description: `Turn notifications on for Nexus Dex in your phone's Settings. (${fired.detail ?? "no detail"})`,
          variant: "destructive",
        });
      }
    }
  };

  const serverPresence = settings?.presenceVisible ?? presenceVisible;

  useEffect(() => {
    if (settings && settings.presenceVisible !== presenceVisible) {
      setPresenceVisible(settings.presenceVisible);
    }
  }, [settings?.presenceVisible]);

  // (Research contributions export/withdraw removed — the section wasn't
  // connected to a real data source.)

  function openManageSubscription() {
    if (typeof window === "undefined") return;
    window.location.href = "itms-apps://apps.apple.com/account/subscriptions";
  }

  return (
    <AppShell pageSpacing>
      {/* No bottom padding here: it used to carry pb-24, which stacked on top of
          AppShell's own .pb-tabbar (88px + safe area) for ~184px of dead space —
          and, being OUTSIDE the rail, it was also where the spine stopped, so
          the line ended a long way above the end of the page. The tab-bar
          clearance now sits INSIDE DotRail, so the spine runs all the way down
          with the content. */}
      <div className="max-w-2xl mx-auto">
        <SectionsForceOpen.Provider value={tourOpen}>
        <DotRail>
          {/* b19-ui-cosmetics: adopt the canonical single pt-12 hero wrapper used
              by Profile / Friends / Trade / Pokedex (PR #271), dropping the
              stacked page-top-pad + back-chip spacer so the eyebrow pill lines up
              pixel-for-pixel with every other top-level menu. */}
          <div className="pt-12 page-hero-top" data-dotrail-skip>
            <MenuHeading
              eyebrow="Settings"
              title="Your"
              greenSuffix="controls."
              subtitle="Account, Pro, appearance, your Nexus Dex, notifications, data, privacy, help."
              data-dotrail-skip
            />
          </div>

        {/* ── Account ────────────────────────────────────────────────── */}
        <Section title="Account" testId="settings-section-account" defaultOpen data-dotrail-dot>
          {/* Trainer name and level: ONE row, edited in place (owner direction,
              2026-10-02). This used to be a read-only name box, a stats box
              repeating the level, and a button that opened a dialog to change
              either: most of a screen for two values. The row is the editor
              now (components/TrainerFields); the tick appears when something
              changed. The name is the identity other players look you up by,
              so it should match the player's Pokémon GO profile. */}
          <FieldLabel>Trainer name and level</FieldLabel>
          {isSignedIn ? (
            <TrainerInlineEditor />
          ) : (
            <div className="mt-1.5 flex items-center gap-2 rounded-xl px-3 py-2.5"
              style={{ background: "var(--surface-elev-2)", border: "1px solid var(--surface-elev-border)" }}
            >
              <AtSign className="w-4 h-4 text-muted-foreground shrink-0" />
              <span data-testid="text-settings-trainer-name" className="flex-1 min-w-0 text-sm truncate">
                {trainer.name || <span className="text-muted-foreground">Not set yet</span>}
              </span>
            </div>
          )}

          {/* What is left of the trainer's profile row, read back from the
              cloud: rows that exist only for trainers who uploaded a profile
              screenshot on an older build, omitted when absent rather than
              shown blank. The level is not repeated here; it is in the row
              above. */}
          {isSignedIn && otherTrainerStats.length > 0 && (
            <>
              <FieldLabel className="mt-4">Trainer stats</FieldLabel>
              <div
                className="mt-1.5 rounded-xl px-3 py-2"
                data-testid="settings-trainer-stats"
                style={{ background: "var(--surface-elev-2)", border: "1px solid var(--surface-elev-border)" }}
              >
                <dl className="grid grid-cols-2 gap-x-4 gap-y-1">
                  {otherTrainerStats.map((r) => (
                    <div key={r.label} className="flex items-baseline justify-between gap-2 min-w-0">
                      <dt className="text-[10px] text-muted-foreground shrink-0">{r.label}</dt>
                      <dd className="text-[11px] font-mono tabular-nums truncate" title={r.value}>
                        {r.value}
                      </dd>
                    </div>
                  ))}
                </dl>
              </div>
            </>
          )}

          {/* Trainer code — editable in place.
              A code rotates, and raid hosting is refused without one
              because a raid whose host cannot be added is one nobody can join.
              Never displayed to anyone else — the only thing that ever reads it
              is the copy button in a lobby the owner opened. */}
          {isSignedIn && (
            <>
              <FieldLabel className="mt-4">Pokémon GO trainer code</FieldLabel>
              <TrainerCodeField />
            </>
          )}

          {/* Email (read-only) */}
          <FieldLabel className="mt-4">Email</FieldLabel>
          <p
            data-testid="text-settings-email"
            className="mt-1 text-sm font-mono"
            style={{ color: account?.email ? "hsl(var(--foreground))" : "hsl(var(--muted-foreground))" }}
          >
            {account?.email ?? "Not signed in"}
          </p>
        </Section>

        {/* ── Nexus Dex Pro ─────────────────────────────────────────── */}
        <Section title="Nexus Dex Pro" testId="settings-section-subscription" data-dotrail-dot>
          <Link
            href="/settings/billing"
            data-testid="link-settings-billing"
            className="rounded-xl p-3 mb-3 flex items-center gap-3 no-underline text-foreground hover-elevate"
            style={{ background: "var(--surface-elev-2)", border: "1px solid var(--surface-elev-border)" }}
          >
            <CreditCard className="w-5 h-5 shrink-0" style={{ color: "hsl(var(--primary))" }} />
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold leading-tight">Why go Pro?</p>
              <p className="text-[12px] text-muted-foreground mt-0.5">
                Basic and Pro side by side, plan and prices, restore purchases
              </p>
            </div>
            <ArrowRight className="w-4 h-4 text-muted-foreground shrink-0" />
          </Link>

          <button
            onClick={openManageSubscription}
            data-testid="button-manage-subscription"
            className="w-full rounded-xl p-3 flex items-center gap-3 hover-elevate text-left"
            style={{ background: "var(--surface-elev-2)", border: "1px solid var(--surface-elev-border)", color: "inherit" }}
          >
            <ExternalLink className="w-5 h-5 shrink-0" style={{ color: "hsl(var(--primary))" }} />
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold leading-tight">Manage subscription</p>
              <p className="text-[12px] text-muted-foreground mt-0.5">
                Opens Apple's subscription settings to cancel or change plan
              </p>
            </div>
          </button>

          {tier === "free" && (
            <div
              className="rounded-xl p-4 mt-3"
              style={{ background: "linear-gradient(135deg, hsl(var(--primary) / 0.15), hsl(var(--card)) 70%)", border: "1px solid hsl(var(--primary) / 0.35)" }}
            >
              <p className="text-sm font-semibold inline-flex items-center gap-1.5">
                <Crown className="w-3.5 h-3.5 text-[hsl(var(--primary))]" /> Try Nexus Dex Pro
              </p>
              <p className="text-[11px] text-muted-foreground mt-1 leading-relaxed">
                {proHeadlineRows().map((r) => r.title).join(", ")}, and more. {formatPlanLine(prices.monthly, "/mo")} or {formatPlanLine(prices.annual, "/yr")}.
              </p>
              <Button
                data-testid="button-settings-try-pro"
                onClick={() => openPaywall("settings")}
                className="mt-3 w-full rounded-full font-semibold"
                style={{ background: "hsl(var(--primary))", color: "hsl(var(--primary-foreground))" }}
              >
                Try Pro
              </Button>
              <button
                type="button"
                data-testid="button-settings-why-pro"
                onClick={openWhyPro}
                className="mt-2 w-full text-center text-[11px] font-semibold"
                style={{ color: "hsl(var(--primary))" }}
              >
                See everything Basic and Pro get, side by side
              </button>
            </div>
          )}
          {tier === "trial" && (
            <div
              className="rounded-xl p-4 mt-3"
              style={{ background: "linear-gradient(135deg, hsl(var(--teal) / 0.15), hsl(var(--card)) 70%)", border: "1px solid hsl(var(--teal) / 0.35)" }}
            >
              <p className="text-sm font-semibold">Trial · {trialSearchesLeft} search left</p>
              <p className="text-[11px] text-muted-foreground mt-1">Upgrade anytime to unlock the Pro feature set.</p>
            </div>
          )}
        </Section>

        {/* ── Appearance ────────────────────────────────────────────── */}
        <Section title="Appearance" data-dotrail-dot>
          <p className="text-sm font-semibold mb-2">Theme</p>
          <div className="grid grid-cols-3 gap-2">
            {([
              { key: "auto",  label: "Auto",  Icon: Monitor },
              { key: "light", label: "Light", Icon: Sun },
              { key: "dark",  label: "Dark",  Icon: Moon },
            ] as { key: ThemeMode; label: string; Icon: typeof Monitor }[]).map(({ key, label, Icon }) => {
              const active = themeMode === key;
              return (
                <button
                  key={key}
                  data-testid={`theme-${key}`}
                  onClick={() => {
                    setThemeMode(key);
                    toast({ title: `Theme set to ${label}` });
                  }}
                  className={`rounded-lg py-2.5 text-xs font-semibold inline-flex items-center justify-center gap-1.5 transition ${active ? "" : "hover-elevate"}`}
                  style={
                    active
                      ? { background: "hsl(var(--primary))", color: "hsl(var(--primary-foreground))" }
                      : { background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", color: "hsl(var(--muted-foreground))" }
                  }
                >
                  <Icon className="w-3.5 h-3.5" /> {label}
                </button>
              );
            })}
          </div>
          <p className="mt-2 text-[11px] text-muted-foreground">Auto follows your system setting.</p>

          {/* ── Team accent (Pro) ─────────────────────────────────────
              Full-app recolour of the brand-green highlights to the
              trainer's GO faction, plus the matching launcher icon on iOS.
              The swatches always render in their OWN colour (literal hexes,
              deliberately not tokens — a picker must show what you'd get,
              not the accent you already have). Free trainers see the
              options; tapping one opens the paywall. */}
          <p className="text-sm font-semibold mt-5 mb-2 inline-flex items-center gap-1.5">
            Team accent
            <Crown className="w-3.5 h-3.5" style={{ color: "var(--accent-fill)" }} />
            <span className="text-[10px] font-bold uppercase tracking-wide text-muted-foreground">Pro</span>
          </p>
          <div className="grid grid-cols-4 gap-2" data-testid="settings-accent">
            {([
              { key: "nexus",    label: "Nexus",    swatch: "#0DA371", ink: "#FFFFFF" },
              { key: "instinct", label: "Instinct", swatch: "#EAB308", ink: "#3B2F00" },
              { key: "valor",    label: "Valor",    swatch: "#DC2626", ink: "#FFFFFF" },
              { key: "mystic",   label: "Mystic",   swatch: "#2563EB", ink: "#FFFFFF" },
            ] as { key: AccentTheme; label: string; swatch: string; ink: string }[]).map(({ key, label, swatch, ink }) => {
              const active = accentTheme === key;
              const locked = key !== "nexus" && !hasProAccess(tier);
              return (
                <button
                  key={key}
                  data-testid={`accent-${key}`}
                  aria-pressed={active}
                  onClick={() => {
                    if (locked) {
                      openPaywall("settings", "Team accents recolour the whole app to your faction. Pro perk.");
                      return;
                    }
                    setAccentTheme(key);
                    toast({
                      title: key === "nexus" ? "Back to Nexus green" : `Team ${label}!`,
                      description: key === "nexus" ? undefined : "The whole app now flies your colours.",
                    });
                  }}
                  className={`rounded-lg py-2.5 text-xs font-semibold inline-flex flex-col items-center justify-center gap-1.5 transition ${active ? "" : "hover-elevate"}`}
                  style={
                    active
                      ? { background: swatch, color: ink }
                      : { background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", color: "hsl(var(--muted-foreground))" }
                  }
                >
                  <span
                    className="w-4 h-4 rounded-full inline-flex items-center justify-center"
                    style={{
                      background: active ? "rgba(255,255,255,0.25)" : swatch,
                      boxShadow: active ? "none" : `0 0 6px ${swatch}80`,
                    }}
                  >
                    {locked && <Lock className="w-2.5 h-2.5" style={{ color: "#fff" }} />}
                  </span>
                  {label}
                </button>
              );
            })}
          </div>
          <p className="mt-2 text-[11px] text-muted-foreground" data-testid="accent-hint">
            Recolours the app's green highlights to your faction
            {hasProAccess(tier) ? "" : " · unlocks with Pro"}. On iPhone the app icon
            changes to match (iOS shows a confirmation when it does).
          </p>

          {/* ── The fourth bottom-bar slot ──────────────────────────────
              Which destination sits beside Calendar. It has changed hands
              twice by code edit; this hands the choice to the trainer. The
              options render from the registry, so adding My Pokemon or Raid
              later needs no change here. */}
          <p className="text-sm font-semibold mt-5 mb-2">Bottom bar shortcut</p>
          <div className="grid grid-cols-2 gap-2" data-testid="settings-bottom-slot">
            {BOTTOM_SLOT_OPTIONS.map((opt) => {
              const active = bottomSlot === opt.key;
              const Icon = opt.icon;
              return (
                <button
                  key={opt.key}
                  data-testid={`bottom-slot-${opt.key}`}
                  aria-pressed={active}
                  onClick={() => {
                    setBottomSlot(opt.key);
                    setBottomSlotState(opt.key);
                    toast({ title: `${opt.label} is now in your bottom bar` });
                  }}
                  className={`rounded-lg py-2.5 text-xs font-semibold inline-flex items-center justify-center gap-1.5 transition ${active ? "" : "hover-elevate"}`}
                  style={
                    active
                      ? { background: "hsl(var(--primary))", color: "hsl(var(--primary-foreground))" }
                      : { background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", color: "hsl(var(--muted-foreground))" }
                  }
                >
                  <Icon className="w-3.5 h-3.5" /> {opt.label}
                </button>
              );
            })}
          </div>
          <p className="mt-2 text-[11px] text-muted-foreground" data-testid="bottom-slot-hint">
            {BOTTOM_SLOT_OPTIONS.find((o) => o.key === bottomSlot)?.hint}. Whichever you
            leave out stays in the main menu. Tip: press and hold the tab itself
            to switch from anywhere.
          </p>
        </Section>

        {/* ── Dex visibility ────────────────────────────────────────── */}
        <DexVisibilitySection data-dotrail-dot />

        {/* ── Scanning ──────────────────────────────────────────────── */}
        <ScanningSection data-dotrail-dot />

        {/* ── Your priorities ──────────────────────────────────────────
            Directly under Account, because it is the other half of "who am
            I": the account block is the identity, this is what that trainer
            is actually playing for.

            The RADAR MOVED HERE FROM PROFILE. It is a settings control that
            happened to live on a dashboard: nothing on it reports progress,
            it only shows what you told us and lets you change it. Profile
            keeps the things that move.

            The old "Tune your Nexus Dex Priorities" row is GONE rather than
            carried across. It opened the same questionnaire as the Tune
            button on the radar itself, so keeping both would put two
            identical links a centimetre apart. */}
        <Section title="Your priorities" testId="settings-section-priorities" data-dotrail-dot>
          <ProfilePriorityRadar />

          <div className="flex items-center justify-between gap-3 mt-4">
            <div className="min-w-0">
              <p className="text-sm font-semibold">Refresh my event picks daily</p>
              <p className="text-[11px] text-muted-foreground">
                Regenerates your top event picks once a day, using the priorities above.
              </p>
            </div>
            <Switch
              checked={settings?.eventFeedAutoRefresh ?? true}
              onCheckedChange={(v) => patchSetting({ eventFeedAutoRefresh: v })}
              disabled={!isSignedIn || settingsMutation.isPending}
              data-testid="toggle-event-feed-auto-refresh"
            />
          </div>

          {/* Friends on event cards (2026-09-04 owner direction): the
              "Friends are looking for" box on the Profile's event cards. It
              already hides itself for a trainer with no friends; this is the
              switch for everyone else. Device-local, default on. */}
          <div className="flex items-center justify-between gap-3 mt-4">
            <div className="min-w-0">
              <p className="text-sm font-semibold">Friends on event cards</p>
              <p className="text-[11px] text-muted-foreground">
                Show what your friends are looking for on each event in your radar.
              </p>
            </div>
            <Switch
              checked={friendTrades}
              onCheckedChange={(v) => {
                setFriendTradesLocal(v);
                setFriendTradesEnabled(v);
              }}
              data-testid="toggle-friend-trades"
            />
          </div>
        </Section>

        {/* ── Notifications (merged: in-app + push, one row per event) ── */}
        <Section
          title="Notifications"
          testId="settings-section-notifications"
          data-dotrail-dot
        >
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0">
              <p className="text-sm font-semibold">Notifications</p>
              <p className="text-[11px] text-muted-foreground leading-snug">
                Master switch. Alerts appear as toasts and in your
                Notifications menu.
              </p>
            </div>
            <Switch
              checked={notifs}
              onCheckedChange={(v) => patchSetting({ notificationsEnabled: v })}
              disabled={!isSignedIn || settingsMutation.isPending}
              data-testid="toggle-notifs"
            />
          </div>

          {/* The automatic event countdowns (2026-10-30): their own group,
              above the per-moment rows, since they are the one choice most
              trainers make here. */}
          {notifs && <LiveEventsSettingsGroup disabled={!isSignedIn} />}

          {notifs && settings && pushPrefs ? (
            <div className="mt-3 border-t border-border pt-3">
              <div className="space-y-3">
                {NOTIF_EVENTS.map((event, index) => (
                  <NotifChannelRow
                    key={event.id}
                    label={event.label}
                    desc={event.desc}
                    channel={channelFor(event)}
                    onChange={(c) => void chooseChannel(event, c)}
                    onPreview={() => void previewEvent(event, index)}
                    pushHint={
                      !event.pushEligible
                        ? "In-app only: you enter these yourself"
                        : event.scope === "account" && getPlatform() === "android" && !remotePushReady
                          // This Android build carries no Firebase config, so
                          // remote push cannot arrive; device reminders are
                          // local notifications and do work.
                          ? "In-app on Android for now"
                          : isNativeShell()
                            ? null
                            : "Push arrives on the installed app"
                    }
                    testId={`notif-${event.id}`}
                  />
                ))}
              </div>

              <p className="mt-3 text-[10px] text-muted-foreground leading-relaxed">
                Choose how each moment reaches you: Off, In-app (a toast plus
                your Notifications menu) or Push (a notification on your phone
                as well). Tap "Send me an example" on any row to see it.
              </p>
            </div>
          ) : notifs && !pushPrefs ? (
            <p className="mt-3 text-[11px] text-muted-foreground">Loading…</p>
          ) : null}
        </Section>


        {/* ── My Pokémon backup ─────────────────────────────────────── */}
        <MyPokemonBackupSection data-dotrail-dot />

        {/* ── Data on this device ──────────────────────────────────────── */}
        {showScanLogExport && (
        <Section title="Data on this device" testId="settings-section-data" data-dotrail-dot>
          <p className="text-[11px] text-muted-foreground mb-3 leading-relaxed">
            Your trainer profile is private by default. We never read your camera roll without you choosing photos for an import.
          </p>

          {/* Scan telemetry export — captures the [dex-scan]/[live-scan] log
              buffer to a file so it can't be truncated by the Xcode console.
              The raw telemetry exposes how the scanner works internally (our
              IP), so it is never simply shown: the admin account has it, and a
              Pro trainer reporting a bug reveals it for this app session with
              seven taps on the version number (2026-09-18). */}
          {showScanLogExport && (
          <>
          <button
            type="button"
            onClick={async () => {
              try {
                const lineCount = dexScanCaptureLineCount();
                if (lineCount === 0) {
                  toast({ title: "No scan log yet", description: "Run a live scan first, then export." });
                  return;
                }
                // Frame dump (2026-09-23): the JPEG frames the scanner ingested
                // ride along as `frame-dump` lines so a report can be replayed.
                const frameText = getDexFrameDumpText();
                const frameCount = dexFrameDumpCount();
                const text = frameText ? `${getDexScanCaptureText()}\n${frameText}` : getDexScanCaptureText();
                const stamp = new Date().toISOString().replace(/[:.]/g, "-");
                const fileName = `nexus_scan_log_${stamp}.txt`;
                if (Capacitor.isNativePlatform()) {
                  const [{ Filesystem, Directory, Encoding }, { Share }] = await Promise.all([
                    import("@capacitor/filesystem"),
                    import("@capacitor/share"),
                  ]);
                  const written = await Filesystem.writeFile({
                    path: fileName,
                    data: text,
                    directory: Directory.Cache,
                    encoding: Encoding.UTF8,
                  });
                  await Share.share({
                    title: "Nexus Dex scan log",
                    text: `Live-scan telemetry (${lineCount.toLocaleString()} lines, ${frameCount} frames).`,
                    files: [written.uri],
                    dialogTitle: "Save the scan log",
                  });
                  toast({ title: "Scan log ready", description: `${lineCount.toLocaleString()} lines. Choose where to save.` });
                } else {
                  const blob = new Blob([text], { type: "text/plain" });
                  const a = document.createElement("a");
                  a.href = URL.createObjectURL(blob);
                  a.download = fileName;
                  document.body.appendChild(a);
                  a.click();
                  a.remove();
                  toast({ title: "Scan log ready", description: `${lineCount.toLocaleString()} lines downloading now.` });
                }
              } catch {
                toast({ title: "Could not export scan log", description: "Try again in a moment." });
              }
            }}
            data-testid="button-scan-log-export"
            className="w-full rounded-xl py-2 text-[11px] font-semibold mt-3"
            style={{
              background: "var(--surface-dark-9)",
              color: "var(--text-ink-bright-faint)",
              border: "1px solid hsl(142 80% 35% / 0.45)",
              minHeight: 40,
            }}
          >
            Export scan log
          </button>
          <p className="text-[10px] mt-2 leading-relaxed" style={{ color: "hsl(142 18% 60%)" }}>
            Diagnostic live-scan telemetry captured in memory (kept off the debug console so it can't freeze). Export after a scan, then{" "}
            <button
              type="button"
              onClick={() => { clearDexScanCapture(); toast({ title: "Scan log cleared" }); }}
              className="underline"
              data-testid="button-scan-log-clear"
            >
              clear it
            </button>.
          </p>
          </>
          )}

          {/* ADMIN ONLY: the store rating ask, reset for a device test
              (2026-10-26). The native sheet is asked for once on a device,
              ever (lib/firstRunPrompts askedOnce), so after one test run a
              tester could not try again, and the owner's Galaxy scan that
              day showed no sheet with nothing to say why. This clears the
              review's asked marks (and the old card's flag) and drops any
              pending moment; the [rating] lines in the exported scan log
              (and `adb logcat -s NexusRating` on Android) say what the gate
              decided. Never shown to trainers: the gate is theirs to keep. */}
          {Boolean(account?.isAdmin) && (
            <div className="mt-4" data-testid="settings-rating-prompts">
              <p className="text-sm font-semibold inline-flex items-center gap-1.5">
                <Star className="w-3.5 h-3.5 text-muted-foreground" />
                Rating prompts
              </p>
              <p className="text-[11px] text-muted-foreground leading-relaxed mt-0.5">
                Admin only. The store rating sheet asks once on a device, ever, after a scan or a
                milestone and a look at the result. Reset to let the next one ask again; the scan log's
                [rating] lines say what the gate decided.
              </p>
              <p className="mt-2 text-[11px] text-muted-foreground" data-testid="rating-last-asked">
                {ratingAskedLabel}
              </p>
              <Button
                variant="outline"
                size="sm"
                data-testid="button-reset-rating-prompts"
                onClick={() => {
                  resetRatingAsk();
                  setRatingAskedLabel(reviewAskedLabel());
                  toast({ title: "Rating prompts reset", description: "The next scan or milestone may ask again on this device." });
                }}
                className="mt-2.5 h-8 text-[11px] font-semibold"
              >
                <RotateCcw className="w-3 h-3 mr-1.5" />
                Reset rating prompts
              </Button>
            </div>
          )}

          {/*
            "Uploaded screenshots" — a heading, a live count and a Delete button —
            used to sit here. It was wired to nothing: the count was a hard-coded
            number in SessionProvider (847 in dev, 0 in prod) and "delete" set
            that number to zero. No screenshot was ever stored under it and none
            was ever removed by it, so the control promised a privacy action the
            app does not perform. Removed 2026-08-12 rather than left as a
            reassurance nobody can rely on.

            An upload is already transient: ScanPokedexModal reads the file the
            picker hands it, holds the data URL in memory for the review sheet,
            and drops it. What "Privacy and data" says above — that we never read
            the camera roll without you choosing photos for an import — remains
            the whole of it.
          */}

          {/*
            STORED SPRITES, and this one is real — which is the whole reason the
            fake block above it went. The count is measured, and the button
            deletes what it names.

            Two on-device stores hold crops lifted off the trainer's own screen:
            the box/detail/catch OCR crops, and the Pokédex grid scan's per-cell
            crops. Neither is displayed anywhere any more, and the dex scan no
            longer captures at all, so what remains is bytes with nothing
            reading them. One control clears both — a trainer does not know
            there are two, and emptying one of them would be the same kind of
            half-truth.
          */}
          <div className="mt-4">
            <p className="text-sm font-semibold inline-flex items-center gap-1.5">
              <ImageOff className="w-3.5 h-3.5 text-muted-foreground" />
              Stored sprite images
            </p>
            <p className="text-[11px] text-muted-foreground leading-relaxed mt-0.5">
              Older scans saved a crop of each Pokémon from your screen. Nothing displays them now
              every sprite comes from our own art, so these are just taking up space.
            </p>
            <p className="mt-2 text-[11px] text-muted-foreground" data-testid="stored-sprite-usage">
              {spriteUsage.count === 0
                ? "Nothing stored on this device."
                : `Holding ${spriteUsage.count.toLocaleString()} image${spriteUsage.count === 1 ? "" : "s"}, about ${formatBytes(spriteUsage.bytes)}.`}
            </p>
            <Button
              variant="outline"
              size="sm"
              data-testid="button-clear-stored-sprites"
              disabled={spriteUsage.count === 0}
              onClick={() => {
                const cleared = clearStoredSprites();
                setSpriteUsage(storedSpriteUsage());
                toast({
                  title: "Sprite images cleared",
                  description: `${cleared.toLocaleString()} image${cleared === 1 ? "" : "s"} removed from this device.`,
                });
              }}
              className="mt-2.5 h-8 text-[11px] font-semibold"
            >
              <Trash2 className="w-3 h-3 mr-1.5" />
              Clear stored sprites
            </Button>
          </div>

        </Section>
        )}

        {/* ── Privacy and legal ───────────────────────────────────────
            Who can see you, then the documents. Two sections until the
            2026-09-11 regroup; one topic, one card. */}
        <Section title="Privacy and legal" testId="settings-section-privacy-security" data-dotrail-dot>
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0">
              <p className="text-sm font-semibold inline-flex items-center gap-1.5">
                <UsersIcon className="w-3.5 h-3.5" /> Visible to friends
              </p>
              <p className="text-[11px] text-muted-foreground">Default on.</p>
            </div>
            <Switch
              checked={serverPresence}
              onCheckedChange={(v) => {
                setPresenceVisible(v);
                patchSetting({ presenceVisible: v });
              }}
              disabled={!isSignedIn || settingsMutation.isPending}
              data-testid="toggle-presence"
            />
          </div>
          <p className="mt-2 text-[11px] text-muted-foreground leading-relaxed">
            {presenceVisible
              ? "Your friends can see your dex completion stats and last-active time."
              : "Your friends see you as 'Private'. Your stats and activity are hidden until you turn this back on."}
          </p>
          <div className="mt-4 border-t border-border pt-3" data-testid="settings-section-legal">
          <button
            onClick={() => showLegal()}
            data-testid="button-settings-legal"
            className="w-full flex items-center gap-2 py-2"
            style={{ color: "inherit", textAlign: "left" }}
          >
            <Shield className="w-4 h-4 shrink-0 text-muted-foreground" />
            <span className="flex-1 text-sm">Privacy notice</span>
            <ArrowRight className="w-4 h-4 text-muted-foreground shrink-0" />
          </button>
          <div className="border-t my-2" style={{ borderColor: "var(--surface-elev-border)" }} />
          <a
            href="https://nexusdex.ai/terms"
            target="_blank"
            rel="noreferrer"
            data-testid="link-settings-terms"
            className="w-full flex items-center gap-2 py-2 no-underline text-foreground"
          >
            <FileText className="w-4 h-4 shrink-0 text-muted-foreground" />
            <span className="flex-1 text-sm">Terms of service</span>
            <ExternalLink className="w-4 h-4 text-muted-foreground shrink-0" />
          </a>
          <div className="border-t my-2" style={{ borderColor: "var(--surface-elev-border)" }} />
          <a
            href="https://nexusdex.ai/privacy"
            target="_blank"
            rel="noreferrer"
            data-testid="link-settings-privacy"
            className="w-full flex items-center gap-2 py-2 no-underline text-foreground"
          >
            <Lock className="w-4 h-4 shrink-0 text-muted-foreground" />
            <span className="flex-1 text-sm">Privacy policy</span>
            <ExternalLink className="w-4 h-4 text-muted-foreground shrink-0" />
          </a>
          <button
            type="button"
            onClick={() => setLegalReaderOpen(true)}
            data-testid="button-open-legal-pack"
            className="mt-2 text-left text-[11px] font-semibold text-[hsl(var(--teal))] hover:underline underline-offset-2"
          >
            Read full legal pack
          </button>
          <div className="border-t mt-3 pt-3" style={{ borderColor: "var(--surface-elev-border)" }}>
            <p
              data-testid="text-settings-app-version"
              className="text-[11px] font-mono text-muted-foreground tabular-nums"
            >
              Nexus Dex {APP_VERSION_STRING}
            </p>
          </div>
          </div>
        </Section>

        {/* ── Help ──────────────────────────────────────────────────────
            Discord and the Walkthroughs page (the getting-started tour plus
            the advanced-feature walks). The three intro slides are gone
            (owner direction, 2026-09-11). */}
        <Section title="Help" testId="settings-section-help" data-dotrail-dot>
          {/* The invite, direct (2026-08-19, user direction). This used to point
              at nexusdex.ai/discord so a rotated invite needed no app release;
              rotating one now means shipping a build, so rotate it here. */}
          <a
            href="https://discord.gg/ZjYMxFFxXQ"
            target="_blank"
            rel="noreferrer"
            data-testid="link-settings-discord"
            className="w-full text-left flex items-center justify-between gap-3 py-2 border-b border-border no-underline text-foreground"
          >
            <div>
              <p className="text-sm font-semibold">Join our Discord</p>
              <p className="text-[11px] text-muted-foreground leading-relaxed">
                Ask questions, find trade partners, share your shinies, and talk
                to the dev directly.
              </p>
            </div>
            <ExternalLink className="w-4 h-4 shrink-0 text-muted-foreground" />
          </a>
          {/* A review, always reachable (2026-09-16). The OS rating sheet asks
              at a milestone or after a scan (lib/storeReview, 2026-10-26); this
              row is the store's own write-review page, for the trainer who
              wants to say more or chose to rate later. Only where a review
              can be left (storeReview.ts): iOS, and Android since Play went
              public. */}
          {canLeaveStoreReview() && (
            <button
              type="button"
              onClick={() => openStoreReview()}
              data-testid="link-settings-review"
              className="w-full text-left flex items-center justify-between gap-3 py-2 border-b border-border text-foreground"
            >
              <div>
                <p className="text-sm font-semibold">Rate Nexus Dex</p>
                <p className="text-[11px] text-muted-foreground leading-relaxed">
                  A 5-star review on the {storeReviewName()} helps other trainers find it.
                </p>
              </div>
              <div className="flex items-center gap-0.5 shrink-0" aria-hidden>
                {[0, 1, 2, 3, 4].map((i) => (
                  <Star key={i} className="w-3 h-3" style={{ color: "var(--accent-fill)", fill: "var(--accent-fill)" }} />
                ))}
              </div>
            </button>
          )}
          <Link
            href="/walkthroughs"
            data-testid="link-settings-walkthroughs"
            className="w-full text-left flex items-center justify-between gap-3 py-2 no-underline text-foreground"
          >
            <div>
              <p className="text-sm font-semibold">Tutorials</p>
              <p className="text-[11px] text-muted-foreground leading-relaxed">
                The getting-started tour, and a guided walk through every advanced feature.
              </p>
            </div>
            <ChevronRight className="w-4 h-4 shrink-0 text-muted-foreground" />
          </Link>
        </Section>

        {/* ── Live Scan diagnostics ─────────────────────────────────────
            Dev/QA surface. Gated behind `import.meta.env.DEV` OR the
            default-OFF diagnostics-menu override (isDiagnosticsMenuEnabled,
            unlocked on prod via #nexus-diag hash / localStorage / VITE env).
            Dev/TestFlight-style builds always get it; end users never see it
            unless Sam explicitly unlocks the override. Kept until the
            live-scan success rate is at 100%, then it can be deleted
            outright. Reachable via the labelled card, the "tap version 5
            times" easter egg, and a deep link (`#/settings?open=diagnostics`)
            from the Live Scan modal.
          ──────────────────────────────────────────────────────────────── */}
        {diagAvailable && (
          <section
            className="rounded-2xl mb-3"
            style={{
              background: "var(--surface-elev-1)",
              border: "1px solid var(--surface-elev-border)",
            }}
            data-testid="settings-section-diagnostics"
          >
            <button
              type="button"
              data-testid="button-toggle-diagnostics"
              onClick={() => setDiagPanelOpen((v) => !v)}
              className="w-full flex items-center gap-3 px-4 py-3 hover-elevate rounded-2xl text-left"
              style={{ color: "inherit" }}
              aria-expanded={diagPanelOpen}
              aria-controls="settings-diagnostics-panels"
            >
              <span
                className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0"
                style={{
                  background: "linear-gradient(135deg, hsl(var(--primary) / 0.22), hsl(var(--primary) / 0.08))",
                  color: "hsl(var(--primary))",
                  border: "1px solid hsl(var(--primary) / 0.35)",
                }}
              >
                <Activity className="w-4 h-4" />
              </span>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold leading-tight">Live Scan diagnostics</p>
                <p className="text-[12px] text-muted-foreground mt-0.5">
                  Paywall / IAP status, broadcast bridge, ingestion health, and recent batches for beta QA.
                </p>
              </div>
              {diagPanelOpen ? (
                <ChevronUp className="w-4 h-4 text-muted-foreground shrink-0" />
              ) : (
                <ChevronDown className="w-4 h-4 text-muted-foreground shrink-0" />
              )}
            </button>
            {diagPanelOpen ? (
              <div id="settings-diagnostics-panels" className="px-4 pb-4">
                <PaywallDiagnosticsPanel />
                <BroadcastDiagnosticsPanel />
                <IngestionDiagnosticsPanel />
                <OcrResultsPanel testid="settings-ocr-results-panel" maxRows={10} />
              </div>
            ) : null}
          </section>
        )}

        {/* ── Demo tier switcher (debug builds only) ────────────────── */}
        {import.meta.env.DEV ? (
        <div
          className="rounded-2xl p-4 mb-3"
          style={{
            background: "linear-gradient(135deg, hsl(43 96% 30% / 0.12), hsl(var(--card)))",
            border: "1px dashed hsl(43 96% 50% / 0.4)",
          }}
        >
          <div className="flex items-center gap-1.5 mb-2">
            <Star className="w-3 h-3 text-[hsl(var(--lucky))]" />
            <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-[hsl(var(--lucky))]">
              Demo · Switch user tier
            </p>
          </div>
          <p className="text-[11px] text-muted-foreground leading-relaxed mb-3">
            Flip the prototype between tiers to see all paywall behaviour.
          </p>
          <div className="grid grid-cols-3 gap-2">
            {(["free", "trial", "pro"] as Tier[]).map((t) => {
              const active = tier === t;
              const label = t === "free" ? "basic" : t;
              return (
                <button
                  key={t}
                  data-testid={`button-set-tier-${t}`}
                  onClick={() => setTier(t)}
                  className={`rounded-lg py-2 text-[10px] font-semibold uppercase tracking-wider transition ${active ? "" : "hover-elevate"}`}
                  style={
                    active
                      ? t === "pro"
                        ? { background: "hsl(var(--primary))", color: "hsl(var(--primary-foreground))" }
                        : t === "trial"
                          ? { background: "linear-gradient(135deg, #5EE2F0, #2BB8C9)", color: "#0B0E1A" }
                          : { background: "hsl(var(--secondary))", color: "hsl(var(--foreground))" }
                      : { background: "hsl(var(--card))", color: "hsl(var(--muted-foreground))", border: "1px solid hsl(var(--border))" }
                  }
                >
                  {label}
                </button>
              );
            })}
          </div>
        </div>
        ) : null}

        {/* ── About collapsible ─────────────────────────────────────── */}
        <button
          data-testid="button-toggle-about"
          onClick={() => setAboutExpanded(!aboutExpanded)}
          className="w-full flex items-center justify-between rounded-2xl px-4 py-3 hover-elevate mb-3"
          style={{ background: "var(--surface-elev-1)", border: "1px solid var(--surface-elev-border)" }}
        >
          <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
            About
          </span>
          {aboutExpanded ? <ChevronUp className="w-4 h-4 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
        </button>
        {aboutExpanded && (
          <div
            className="rounded-2xl p-4 mb-3"
            style={{ background: "var(--surface-elev-1)", border: "1px solid var(--surface-elev-border)" }}
          >
            <div className="text-[11px] text-muted-foreground space-y-2 leading-relaxed">
              <p>
                Nexus Dex{" "}
                <span
                  className="font-mono text-foreground select-none"
                  data-testid="button-version-tap"
                  // Tap the version number 7 times to unlock the Live Scan
                  // diagnostics surface on any build (including prod
                  // TestFlight). The gesture is always live: it writes the
                  // default-OFF diagnostics-menu override to localStorage,
                  // clears the resolver cache, flips the React unlock trigger
                  // so the gated section re-renders, opens the panel, and
                  // confirms with a toast. End users who never perform the
                  // deliberate 7-tap gesture still see nothing. This does NOT
                  // unlock the DEV-only demo tier switcher.
                  onClick={() => {
                    const next = diagTapCount + 1;
                    if (next >= 7) {
                      setDiagTapCount(0);
                      if (hasProAccess(tier)) {
                        unlockScanLogExport();
                        setScanLogUnlocked(true);
                      }
                      try {
                        window.localStorage.setItem(DIAGNOSTICS_MENU_LS_KEY, "1");
                      } catch {
                        // ignore (private mode / storage disabled)
                      }
                      __resetDiagnosticsMenuCache();
                      setDiagUnlocked(true);
                      setDiagPanelOpen(true);
                      toast({
                        title: "Diagnostics unlocked",
                        description: hasProAccess(tier)
                          ? "Live Scan diagnostics and Export scan log are now available in Settings until the app is closed."
                          : "Live Scan diagnostics are now available in Settings.",
                      });
                    } else {
                      setDiagTapCount(next);
                    }
                  }}
                >
                  {APP_VERSION_STRING}
                </span>
              </p>
              <p>
                Pokemon and Pokemon GO are trademarks of Nintendo, Game Freak, Niantic, and Scopely.
                <span className="text-foreground/90"> Nexus Dex is unaffiliated.</span> Not endorsed by Niantic / Scopely / Nintendo / The Pokemon Company.
              </p>
              <p>
                Main-dex species artwork is original Nexus Dex glyph design.
                Sub-dex form sprites (Vivillon patterns, Unown letters, Spinda
                patterns, regional variants etc.) are sourced from{" "}
                <a
                  href="https://pokeapi.co"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline"
                  data-testid="settings-credit-pokeapi"
                >
                  PokeAPI
                </a>{" "}
                and (for Spinda){" "}
                <a
                  href="https://pokemondb.net"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline"
                  data-testid="settings-credit-pokemondb"
                >
                  pokemondb
                </a>. Full credits are listed in{" "}
                <a
                  href="https://github.com/fordsam187/NexusDex/blob/main/CREDITS.md"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline"
                  data-testid="settings-credit-credits-md"
                >
                  CREDITS.md
                </a>.
              </p>
            </div>
          </div>
        )}

        {/* ── Account actions (bottom danger zone) ──────────────────────
            Sign out and Delete account are the two "exit" actions, grouped
            together in one destructive-styled card at the very bottom of
            Settings so they read as terminal actions and aren't tap-bait
            mid-page. Sign out was previously buried in Privacy & Security.

            Apple App Store guideline 5.1.1(v) requires apps that create an
            account to also let users delete it from inside the app. The
            backend already runs a 15-day soft-delete with a hard purge sweep;
            this UI is the trainer-facing entry point.
          ──────────────────────────────────────────────────────────────── */}
        {isSignedIn ? (
          <section
            className="rounded-2xl p-4 mb-3 overflow-hidden"
            style={{
              background: "var(--surface-elev-1)",
              border: "1px solid hsl(354 78% 56% / 0.32)",
              // Its own compositing layer, clipped (2026-09-11 owner report):
              // on the iPhone this card's pink hairlines were turning up as
              // stray lines behind other cards further up the page and moving
              // as it scrolled, a WKWebView tile smear. Isolating the card
              // keeps its pixels inside its own box.
              isolation: "isolate",
              transform: "translateZ(0)",
            }}
            data-testid="settings-section-account-actions"
          >
            {/* Sign out — non-destructive exit, sits above delete. */}
            <button
              type="button"
              onClick={() => signout()}
              data-testid="button-settings-signout"
              className="w-full flex items-center justify-center gap-2 rounded-xl py-2.5 text-sm font-semibold"
              style={{
                background: "hsl(354 78% 30% / 0.12)",
                color: "hsl(354 78% 80%)",
                border: "1px solid hsl(354 78% 56% / 0.5)",
                minHeight: 40,
              }}
            >
              <LogOut className="w-4 h-4" />
              Sign out
            </button>

          {/* Reset Nexus Dex (b13c.7): between Sign out and Delete account
              (owner direction, 2026-09-11), the middle of the three exits. */}
          <div className="mt-4 pt-4" style={{ borderTop: "1px solid hsl(354 78% 56% / 0.24)" }}>
            <p className="text-sm font-semibold inline-flex items-center gap-1.5">
              <Trash2 className="w-3.5 h-3.5 text-muted-foreground" />
              Reset Nexus Dex
            </p>
            <p className="text-[11px] text-muted-foreground leading-relaxed mt-0.5">
              Clear every scanned and manually-marked Pokédex entry on this device
              and start from a clean slate. Your account, screenshots, and other
              data are untouched. This cannot be undone.
            </p>
            {!confirmResetDex ? (
              <Button
                variant="outline"
                size="sm"
                data-testid="button-reset-nexus-dex"
                onClick={() => setConfirmResetDex(true)}
                className="mt-2.5 h-8 text-[11px] font-semibold"
              >
                <Trash2 className="w-3 h-3 mr-1.5" />
                Reset Nexus Dex
              </Button>
            ) : (
              <div className="mt-2.5 flex items-center gap-2">
                <Button
                  variant="destructive"
                  size="sm"
                  data-testid="button-confirm-reset-nexus-dex"
                  disabled={resettingDex}
                  onClick={async () => {
                    setResettingDex(true);
                    try {
                      await resetNexusDex(apiRequest);
                    } finally {
                      setResettingDex(false);
                      setConfirmResetDex(false);
                    }
                    toast({
                      title: "Nexus Dex reset",
                      description: "All Pokédex entries on this device have been cleared.",
                    });
                  }}
                  className="h-8 text-[11px] font-semibold"
                >
                  {resettingDex ? <Loader2 className="w-3 h-3 mr-1.5 animate-spin" /> : <Trash2 className="w-3 h-3 mr-1.5" />}
                  Yes, reset my dex
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  data-testid="button-cancel-reset-nexus-dex"
                  disabled={resettingDex}
                  onClick={() => setConfirmResetDex(false)}
                  className="h-8 text-[11px] font-semibold"
                >
                  Cancel
                </Button>
              </div>
            )}
          </div>

            <div className="mt-4 mb-4 h-px w-full" style={{ background: "hsl(354 78% 56% / 0.24)" }} aria-hidden />

            <p className="text-[10px] font-bold uppercase tracking-[0.18em] mb-2" style={{ color: "hsl(354 78% 70%)" }}>
              Delete account
            </p>
            {accountDeleteScheduled ? (
              <>
                <p className="text-sm font-semibold" style={{ color: "hsl(354 78% 88%)" }}>
                  Deletion scheduled
                </p>
                <p className="mt-1 text-[12px] leading-relaxed text-muted-foreground">
                  Your Nexus Dex account is queued for permanent removal on{" "}
                  <span className="font-mono text-foreground">
                    {new Date(accountDeleteScheduled.deleteAt).toLocaleDateString(undefined, {
                      year: "numeric", month: "short", day: "numeric",
                    })}
                  </span>
                  . Signing back in within the next 15 days will cancel it automatically.
                </p>
                <Button
                  onClick={cancelAccountDeletion}
                  disabled={accountDeletePending}
                  variant="outline"
                  data-testid="button-account-delete-cancel"
                  className="mt-3 w-full rounded-xl"
                >
                  {accountDeletePending ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : null}
                  Keep my account
                </Button>
              </>
            ) : !accountDeleteOpen ? (
              <>
                <p className="text-[12px] text-muted-foreground leading-relaxed">
                  Permanently delete your Nexus Dex account, including your trainer
                  profile, dex entries, scan history, and friend connections.
                  Cancellation window: 15 days. Active subscriptions are not refunded:
                  cancel from Manage subscription first.
                </p>
                <Button
                  onClick={() => setAccountDeleteOpen(true)}
                  variant="outline"
                  data-testid="button-account-delete-open"
                  className="mt-3 w-full rounded-xl"
                  style={{
                    background: "hsl(354 78% 30% / 0.12)",
                    color: "hsl(354 78% 80%)",
                    borderColor: "hsl(354 78% 56% / 0.5)",
                  }}
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete my account
                </Button>
              </>
            ) : (
              <>
                <p className="text-sm font-semibold" style={{ color: "hsl(354 78% 88%)" }}>
                  Are you sure?
                </p>
                <ul className="mt-2 text-[12px] text-muted-foreground leading-relaxed space-y-1 list-disc pl-4">
                  <li>Your trainer profile, dex entries, journal and scan history will be erased.</li>
                  <li>You have 15 days to change your mind: sign back in to cancel.</li>
                  <li>
                    Active subscriptions are billed by {getPlatform() === "android" ? "Google Play" : "Apple"}, not us.
                    Cancel in Manage subscription if you haven't already.
                  </li>
                </ul>
                <LeaveReasonPicker
                  reason={leaveReason}
                  detail={leaveDetail}
                  onReason={setLeaveReason}
                  onDetail={setLeaveDetail}
                  disabled={accountDeletePending}
                  onResetInstead={() => {
                    closeAccountDelete();
                    setConfirmResetDex(true);
                    // After the reset confirm has rendered, bring it into view.
                    setTimeout(() => {
                      document
                        .querySelector('[data-testid="button-confirm-reset-nexus-dex"]')
                        ?.scrollIntoView({ behavior: "smooth", block: "center" });
                    }, 50);
                  }}
                />
                <div className="mt-3 flex flex-col sm:flex-row gap-2">
                  <Button
                    variant="destructive"
                    onClick={requestAccountDeletion}
                    disabled={accountDeletePending}
                    data-testid="button-account-delete-confirm"
                    className="flex-1 rounded-xl"
                  >
                    {accountDeletePending ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Trash2 className="w-4 h-4 mr-2" />
                    )}
                    Yes, delete my account
                  </Button>
                  <Button
                    variant="outline"
                    onClick={closeAccountDelete}
                    disabled={accountDeletePending}
                    data-testid="button-account-delete-cancel-prompt"
                    className="flex-1 rounded-xl"
                  >
                    Keep my account
                  </Button>
                </div>
              </>
            )}
          </section>
        ) : null}

        {/* Clearance for the tab bar's protruding centre coin, INSIDE the rail
            so the spine covers it. Untagged, so it gets no dot. */}
        <div className="h-8" aria-hidden />
        </DotRail>
        </SectionsForceOpen.Provider>
      </div>

      <LegalReader open={legalReaderOpen} onClose={() => setLegalReaderOpen(false)} />

    </AppShell>
  );
}

/**
 * Every Settings card collapses (owner direction, 2026-09-11): the page had
 * grown to a dozen cards and reads as a table of contents now. Open state is
 * remembered per card. While the guided tour is up every card is held open so
 * its spotlight lands on a real control rather than a folded header.
 */
const SECTIONS_OPEN_KEY = "nexus.settings.open.v1";
const SectionsForceOpen = createContext(false);

function readOpenSections(): Record<string, boolean> {
  try {
    const raw = localStorage.getItem(SECTIONS_OPEN_KEY);
    return raw ? (JSON.parse(raw) as Record<string, boolean>) : {};
  } catch {
    return {};
  }
}

function writeOpenSection(key: string, open: boolean): void {
  try {
    localStorage.setItem(SECTIONS_OPEN_KEY, JSON.stringify({ ...readOpenSections(), [key]: open }));
  } catch {
    /* remembered for this visit only */
  }
}

function Section({
  title, children, testId, defaultOpen = false, "data-dotrail-dot": dotrailDot,
}: {
  title: string;
  children: React.ReactNode;
  testId?: string;
  /** Open on a fresh install; a remembered choice wins. */
  defaultOpen?: boolean;
  "data-dotrail-dot"?: boolean;
}) {
  const key = testId ?? title;
  const forceOpen = useContext(SectionsForceOpen);
  const [open, setOpen] = useState<boolean>(() => readOpenSections()[key] ?? defaultOpen);
  const shown = open || forceOpen;
  return (
    <section
      className="rounded-2xl px-4 py-2.5 mb-3"
      style={{ background: "var(--surface-elev-1)", border: "1px solid var(--card-border-accent)" }}
      data-testid={testId}
      data-dotrail-dot={dotrailDot ? "" : undefined}
      data-open={shown ? "" : undefined}
    >
      <button
        type="button"
        aria-expanded={shown}
        data-testid={testId ? `${testId}-toggle` : undefined}
        onClick={() => {
          setOpen((v) => {
            writeOpenSection(key, !v);
            return !v;
          });
        }}
        className="w-full flex items-center justify-between gap-3 py-0.5 text-left"
      >
        <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-[hsl(var(--primary))]">
          {title}
        </span>
        <ChevronDown
          className={`w-4 h-4 shrink-0 text-muted-foreground transition-transform ${shown ? "rotate-180" : ""}`}
          aria-hidden
        />
      </button>
      {shown && <div className="pt-2.5 pb-1.5">{children}</div>}
    </section>
  );
}

// Variant label mapping. Mirrors the constant in Pokedex.tsx; kept inline
// here intentionally (extracting to a shared file would expand the diff for
// marginal gain). The "All" tab is locked on and never appears as a row.
function DexVisibilitySection({
  "data-dotrail-dot": dotrailDot,
}: { "data-dotrail-dot"?: boolean } = {}) {
  // Re-render on cross-surface visibility changes so the switches never drift
  // from the store (e.g. toggled from the Pokedex page or another tab).
  const [, force] = useState(0);
  useEffect(() => {
    if (typeof window === "undefined") return;
    const refresh = () => force((n) => n + 1);
    window.addEventListener(VISIBILITY_EVENT, refresh);
    window.addEventListener(CUSTOM_DEX_LINKAGE_EVENT, refresh);
    return () => {
      window.removeEventListener(VISIBILITY_EVENT, refresh);
      window.removeEventListener(CUSTOM_DEX_LINKAGE_EVENT, refresh);
    };
  }, []);

  // Order AND names track the admin dex-config: orderedVariants() applies the
  // admin tab-order override, and getVariantLabel() applies the admin rename,
  // so this list is always the same sequence and labels the trainer sees in the
  // dex carousel (and never drifts back to a hardcoded copy).
  const toggleable = orderedVariants().filter(
    (v) => !LOCKED_ON_VARIANTS.includes(v),
  );

  return (
    <Section title="Dex visibility" testId="settings-section-dex-visibility" data-dotrail-dot={dotrailDot}>
      {/* b19-feat-linked-dex: propagation toggle for custom dexes. */}
      <div className="flex items-center justify-between py-2 border-b border-border mb-3">
        <div className="pr-3">
          <span className="text-sm font-semibold">Link custom dexes to base dexes</span>
          <p className="text-[11px] text-muted-foreground leading-relaxed mt-0.5">
            Auto-tick Shiny and Perfect when you add a Shiny Perfect. Auto-tick
            Shiny and Lucky when you add a Shiny Lucky.
          </p>
        </div>
        <Switch
          checked={isCustomDexLinkageEnabled()}
          onCheckedChange={setCustomDexLinkageEnabled}
          data-testid="custom-dex-linkage-toggle"
          aria-label="Link custom dexes to base dexes"
        />
      </div>
      <p className="text-[11px] text-muted-foreground mb-3 leading-relaxed">
        Hide custom or unused dex tabs from the Pokedex page. The Main tab cannot be hidden.
      </p>
      <div className="flex flex-col">
        {toggleable.map((v) => (
          <div
            key={v}
            className="flex items-center justify-between py-2 border-b border-border last:border-b-0"
          >
            <span className="text-sm font-semibold">{getVariantLabel(v)}</span>
            <Switch
              checked={isDexVisible(v)}
              onCheckedChange={(checked) => setDexVisible(v, checked)}
              data-testid={`dex-visibility-toggle-${v}`}
              aria-label={`Show ${getVariantLabel(v)} dex`}
            />
          </div>
        ))}
      </div>
    </Section>
  );
}

// Scan behaviour switches (2026-08-28 ask): governs the My Pokémon → Pokédex
// cross-write. The gate itself lives inside crossWriteMyPokemonToDex, so this
// switch flips every save path (live scan, screenshots, quick add, imports,
// the detail page's variant flags) at once.
function ScanningSection({
  "data-dotrail-dot": dotrailDot,
}: { "data-dotrail-dot"?: boolean } = {}) {
  const [autoUpdate, setAutoUpdate] = useState(() => isDexAutoUpdateEnabled());
  const [autoScanOnOpen, setAutoScanOnOpenLocal] = useState(() => isAutoScanOnOpenEnabled());
  const { toast } = useToast();
  // The album sweep exists on iOS builds with the ScreenshotLibrary plugin;
  // elsewhere Auto Scan is the picker flow and there is nothing to run on
  // open, so the row stays hidden rather than promising a no-op.
  const sweepAvailable = isScreenshotSweepAvailable();

  const onAutoScanOnOpen = (v: boolean) => {
    if (!v) {
      setAutoScanOnOpenLocal(false);
      setAutoScanOnOpenEnabled(false);
      return;
    }
    // Switching it on from here is the tap that justifies the Photos prompt
    // (a launch never asks). A refused grant leaves the switch off and says
    // where the fix is, exactly as the Upload menu does.
    void (async () => {
      const status = await ensureScreenshotAccess();
      if (status === "denied") {
        setAutoScanOnOpenLocal(false);
        toast({
          title: "Photos access is off",
          description: "Allow photo access in Settings so Auto Scan can run when the app opens.",
          action: (
            <ToastAction altText="Open Settings" onClick={() => void openAppSettings()}>
              Open Settings
            </ToastAction>
          ),
        });
        return;
      }
      if (status === "restricted") {
        setAutoScanOnOpenLocal(false);
        toast({
          title: "Photos access is blocked on this device",
          description:
            "Screen Time or parental controls block photo library access, so Auto Scan can't run on open.",
        });
        return;
      }
      setAutoScanOnOpenLocal(true);
      setAutoScanOnOpenEnabled(true);
    })();
  };

  return (
    <Section title="Scanning" testId="settings-section-scanning" data-dotrail-dot={dotrailDot}>
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm font-semibold">Update Pokédex from scans</p>
          <p className="text-[11px] text-muted-foreground leading-snug">
            A scanned or imported Pokémon ticks its species in your dexes
            automatically (Shiny, Lucky and the rest included). Additive only:
            nothing is ever un-caught.
          </p>
        </div>
        <Switch
          checked={autoUpdate}
          onCheckedChange={(v) => {
            setAutoUpdate(v);
            setDexAutoUpdateEnabled(v);
          }}
          data-testid="toggle-dex-auto-update"
        />
      </div>
      {sweepAvailable && (
        <div className="flex items-center justify-between gap-3 mt-4">
          <div className="min-w-0">
            <p className="text-sm font-semibold">Auto Scan when the app opens</p>
            <p className="text-[11px] text-muted-foreground leading-snug">
              Checks your Screenshots album for new Pokémon GO screens each time
              Nexus Dex opens or comes back to the front, and saves them in the
              background. Turns itself on the first time you run Auto Scan from
              Upload.
            </p>
          </div>
          <Switch
            checked={autoScanOnOpen}
            onCheckedChange={onAutoScanOnOpen}
            data-testid="toggle-auto-scan-on-open"
          />
        </div>
      )}
    </Section>
  );
}

// My Pokémon cloud backup (2026-08-28): the Backup x Pro policy the dex sync
// uses. Every signed-in trainer's records are pushed and stored; Pro unlocks
// pulling the copy back down onto a fresh install or second device. The
// MyPokemonSyncer (App.tsx) does the actual work; this card is the switch,
// the status line, and the on-demand push.
function MyPokemonBackupSection({
  "data-dotrail-dot": dotrailDot,
}: { "data-dotrail-dot"?: boolean } = {}) {
  const { isSignedIn } = useAuth();
  const { tier } = useSession();
  const { toast } = useToast();
  const { enabled, meta } = useMyPokemonBackupStatus();
  const isPro = hasProAccess(tier);
  const count = myPokemonCount();

  const statusLine = !isSignedIn
    ? "Sign in and your Pokémon back up automatically."
    : !enabled
      ? "Backup is off. Your Pokémon live on this device only."
      : meta.lastSyncAt != null
        ? `${meta.backedUpCount} backed up · last ${new Date(meta.lastSyncAt).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}`
        : count > 0
          ? "Backing up shortly."
          : "Nothing to back up yet.";

  return (
    <Section
      title="My Pokémon backup"
      testId="settings-section-my-pokemon-backup"
      data-dotrail-dot={dotrailDot}
    >
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm font-semibold">Back up My Pokémon</p>
          <p
            className="text-[11px] text-muted-foreground leading-snug"
            data-testid="my-pokemon-backup-status"
          >
            {statusLine}
          </p>
        </div>
        <Switch
          checked={enabled}
          onCheckedChange={(v) => setMyPokemonBackupEnabled(v)}
          data-testid="toggle-my-pokemon-backup"
        />
      </div>
      <p className="mt-2 text-[11px] text-muted-foreground leading-relaxed">
        {isPro
          ? "Your scanned Pokémon, IVs and all, are stored with your account and restore automatically when you sign in on a new device."
          : meta.proLockedCount != null && meta.proLockedCount > 0
            ? `Your ${meta.proLockedCount} backed-up Pokémon are stored safely. Restoring them to a new device is included with Pro.`
            : "Backups are stored for every signed-in trainer. Restoring to a new device is included with Pro."}
      </p>
      {isSignedIn && enabled && (
        <Button
          variant="outline"
          size="sm"
          className="mt-3"
          data-testid="my-pokemon-backup-now"
          onClick={() => {
            window.dispatchEvent(new Event(MY_POKEMON_BACKUP_NOW_EVENT));
            toast({ title: "Backing up your Pokémon now." });
          }}
        >
          Back up now
        </Button>
      )}
    </Section>
  );
}

function FieldLabel({ children, className = "" }: { children: React.ReactNode; className?: string }) {
  return (
    <label className={`block text-[10px] uppercase tracking-[0.14em] text-muted-foreground ${className}`}>
      {children}
    </label>
  );
}

/**
 * Merged notification row: label + description on their OWN full-width row, and
 * a full-width segmented Off / In-app control on the row BELOW — never
 * side-by-side, so text can't disappear behind the control on narrow screens.
 *
 * Push was removed 2026-07-29 (it never worked on iOS/Android); every event is
 * in-app only, so the control is a two-way Off / In-app toggle. Active state
 * uses the theme `--primary` accent (per-theme in index.css) — NO hardcoded
 * colours, so light/dark/v2 are all respected.
 */
function NotifChannelRow({
  label,
  desc,
  channel,
  onChange,
  onPreview,
  pushHint,
  testId,
}: {
  label: string;
  desc: string;
  channel: NotifChannel;
  onChange: (c: NotifChannel) => void;
  /** Fires the row's example through its current channel. */
  onPreview?: () => void;
  /** When set, the Push segment is disabled and this explains why. */
  pushHint?: string | null;
  testId: string;
}) {
  // Push is back (2026-09-11): every row offers Off / In-app / Push.
  const segments: { key: NotifChannel; label: string; disabled?: boolean }[] = [
    { key: "off", label: "Off" },
    { key: "in-app", label: "In-app" },
    { key: "push", label: "Push", disabled: !!pushHint },
  ];
  return (
    <div className="py-1">
      {/* Row 1: label + description, full width. */}
      <div className="min-w-0">
        <p className="text-xs font-semibold">{label}</p>
        <p className="text-[10px] text-muted-foreground leading-snug">{desc}</p>
      </div>
      {/* Row 2: segmented control, full width on its own line. */}
      <div
        role="group"
        aria-label={`${label} delivery`}
        className="mt-2 grid grid-cols-3 gap-1 rounded-lg p-1"
        style={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))" }}
      >
        {segments.map(({ key, label: segLabel, disabled }) => {
          const active = channel === key;
          return (
            <button
              key={key}
              type="button"
              onClick={() => onChange(key)}
              disabled={disabled}
              title={disabled && pushHint ? pushHint : undefined}
              data-testid={`${testId}-${key}`}
              aria-pressed={active}
              className={`rounded-md py-1.5 text-[11px] font-semibold transition ${
                active ? "" : "hover-elevate"
              }`}
              style={
                active
                  ? {
                      background: "hsl(var(--primary))",
                      color: "hsl(var(--primary-foreground))",
                    }
                  : {
                      background: "transparent",
                      color: "hsl(var(--muted-foreground))",
                      cursor: "pointer",
                    }
              }
            >
              {segLabel}
            </button>
          );
        })}
      </div>
      {onPreview && (
        <button
          type="button"
          onClick={onPreview}
          className="mt-1.5 text-[10px] font-bold"
          style={{ color: "var(--accent-fill)" }}
          data-testid={`${testId}-preview`}
        >
          Send me an example
        </button>
      )}
    </div>
  );
}

function V2ThemeToggleRow() {
  const v2On = useV2Theme();
  return (
    <div className="mt-3 pt-3 border-t" style={{ borderColor: "var(--surface-elev-border)" }}>
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm font-semibold">Design v2 (preview)</p>
          <p className="text-[11px] text-muted-foreground">
            Marketing-look skin, neon green on near-black.
          </p>
        </div>
        <Switch
          checked={v2On}
          onCheckedChange={(v) => setV2Theme(v)}
          data-testid="toggle-design-v2"
        />
      </div>
    </div>
  );
}
